/**
 * Layout Grouping Utilities
 *
 * Utilities for grouping layout items by type and language direction.
 * Used to separate text content from widgets for proper RTL handling.
 */

import type { LayoutItem } from '../../transformers/zod-schemas';
import { JSON_TYPES } from '../../constants';
import { itemContainsArabic, itemContainsLatin } from './bidi-detection';

/**
 * Type for grouped line items
 */
export type LineSubGroup = {
  /** Layout items in this group */
  items: LayoutItem[];
  /** Whether this is a text group (vs widget group) */
  isTextGroup: boolean;
  /** Whether group contains Arabic text */
  hasArabic: boolean;
  /** Whether group contains Latin text */
  hasLatin: boolean;
  /** Whether RTL direction should be applied */
  shouldApplyRTL: boolean;
};

/**
 * Check if an item is a text-like item (not a widget)
 *
 * Text-like items can be grouped together for RTL handling.
 * Widgets should remain separate to maintain LTR direction.
 *
 * @param item - The layout item to check
 * @returns true if item is text-like (not a widget)
 */
export function isTextItem(item: LayoutItem): boolean {
  return item.type === JSON_TYPES.TEXT ||
         item.type === JSON_TYPES.HIGHLIGHTED_TEXT ||
         item.type === JSON_TYPES.BOLD_TEXT ||
         item.type === JSON_TYPES.COLOR_TEXT ||
         item.type === JSON_TYPES.OPERATOR ||
         item.type === JSON_TYPES.SPACE ||
         item.type === JSON_TYPES.GROUP;
}

/**
 * Group line items into text segments and widgets
 *
 * Text segments can have RTL applied if they're purely Arabic.
 * Widgets remain in separate groups and always render LTR.
 *
 * @param items - Array of layout items to group
 * @returns Array of grouped items with metadata
 *
 * @example
 * ```typescript
 * const items = [
 *   { type: 'text', value: 'مرحبا' },  // Arabic text
 *   { type: 'widget-ref', widgetId: 'input1' },  // Widget
 *   { type: 'text', value: 'hello' }  // Latin text
 * ];
 *
 * const groups = groupLineItems(items);
 * // [
 * //   { items: [text], isTextGroup: true, shouldApplyRTL: true },
 * //   { items: [widget], isTextGroup: false, shouldApplyRTL: false },
 * //   { items: [text], isTextGroup: true, shouldApplyRTL: false }
 * // ]
 * ```
 */
export function groupLineItems(items: LayoutItem[]): LineSubGroup[] {
  const groups: LineSubGroup[] = [];
  let currentTextGroup: LayoutItem[] = [];

  for (const item of items) {
    if (isTextItem(item)) {
      currentTextGroup.push(item);
    } else {
      // It's a widget - flush current text group first
      if (currentTextGroup.length > 0) {
        const hasArabic = currentTextGroup.some(itemContainsArabic);
        const hasLatin = currentTextGroup.some(itemContainsLatin);
        // Only apply RTL if there's Arabic and NO Latin (pure Arabic)
        const shouldApplyRTL = hasArabic && !hasLatin;
        groups.push({ items: currentTextGroup, isTextGroup: true, hasArabic, hasLatin, shouldApplyRTL });
        currentTextGroup = [];
      }
      // Add widget as its own group
      groups.push({ items: [item], isTextGroup: false, hasArabic: false, hasLatin: false, shouldApplyRTL: false });
    }
  }

  // Flush remaining text group
  if (currentTextGroup.length > 0) {
    const hasArabic = currentTextGroup.some(itemContainsArabic);
    const hasLatin = currentTextGroup.some(itemContainsLatin);
    // Only apply RTL if there's Arabic and NO Latin (pure Arabic)
    const shouldApplyRTL = hasArabic && !hasLatin;
    groups.push({ items: currentTextGroup, isTextGroup: true, hasArabic, hasLatin, shouldApplyRTL });
  }

  return groups;
}
